import { alpha, styled } from '@mui/material/styles';
import { Box, Grid, Text } from 'components/ui';
import appTheme from 'theme';

interface AuthFlowLayoutProps {
    isAuthFlowStep?: boolean;
}

export const PDF_PRIMARY_COLOR = appTheme.palette.primary.main;
export const PDF_BODY_TEXT_COLOR = appTheme.palette.grey[900];
export const PDF_MUTED_TEXT_COLOR = appTheme.palette.grey[600];

// Main BackupCodes styles
export const Container = styled(Box, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<AuthFlowLayoutProps>(({theme, isAuthFlowStep}) => ({
  backgroundColor: isAuthFlowStep ? 'transparent' : theme.palette.background.paper,
  boxSizing: 'border-box',
  padding: isAuthFlowStep ? 0 : theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    padding: isAuthFlowStep ? 0 : theme.spacing(3),
  },
}));

export const ButtonsGrid = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<AuthFlowLayoutProps>(({theme, isAuthFlowStep}) => ({
  marginTop: theme.spacing(3),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    marginTop: isAuthFlowStep ? theme.spacing(2) : theme.spacing(3),
  },
}));

export const ActionButtonSlot = styled(Grid)({});

export const DownloadButtonSlot = styled(ActionButtonSlot)({});

export const GenerateActionGroup = styled(Grid)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  width: '100%',
}));

export const InlineActionsGroup = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<AuthFlowLayoutProps>(({theme, isAuthFlowStep}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: isAuthFlowStep ? 'space-between' : 'flex-end',
  marginTop: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    alignItems: 'center',
    flexWrap: isAuthFlowStep ? 'nowrap' : 'wrap',
    justifyContent: isAuthFlowStep ? 'space-between' : 'flex-end',
  },
  [theme.breakpoints.down('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: isAuthFlowStep ? 'space-between' : 'flex-end',
    rowGap: theme.spacing(1),
  },
}));

export const SecondaryActionsGroup = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<AuthFlowLayoutProps>(({theme, isAuthFlowStep}) => ({
  alignItems: 'center',
  columnGap: theme.spacing(1.5),
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: isAuthFlowStep ? 'flex-start' : 'flex-end',
  rowGap: theme.spacing(1),
  width: isAuthFlowStep ? 'auto' : '100%',
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
    width: isAuthFlowStep ? 'auto' : '100%',
  },
}));

export const PrimaryActionGroup = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<AuthFlowLayoutProps>(({theme, isAuthFlowStep}) => ({
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'flex-end',
  marginLeft: 'auto',
  marginTop: isAuthFlowStep ? 0 : theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    marginTop: 0,
  },
}));

// BackupCodesInfo styles
interface ItemsContainerProps {
    isAuthFlowStep?: boolean;
}

export const ItemsContainer = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<ItemsContainerProps>(({theme, isAuthFlowStep}) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  border: `1px solid ${theme.palette.primary.main}`,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  marginTop: isAuthFlowStep ? '28px' : theme.spacing(5),
  rowGap: theme.spacing(0.5),

  // One code per line
  '@media print': {
    rowGap: 0,
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: isAuthFlowStep ? '28px' : 0,
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

interface ItemProps {
    isAuthFlowStep?: boolean;
}

export const Item = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<ItemProps>(({theme, isAuthFlowStep}) => ({
  textAlign: 'center',
  padding: isAuthFlowStep ? theme.spacing(1.25, 1) : theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: isAuthFlowStep ? theme.spacing(0.75, 1) : theme.spacing(1.5),
  },

  ...(isAuthFlowStep
    ? {
      [theme.breakpoints.up('sm')]: {
        maxWidth: '50%',
        flexBasis: '50%',
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: `${100 / 3}%`,
        flexBasis: `${100 / 3}%`,
      },
      [theme.breakpoints.up('lg')]: {
        maxWidth: `${100 / 5}%`,
        flexBasis: `${100 / 5}%`,
      },
    }
    : {
      [theme.breakpoints.up('md')]: {
        maxWidth: `${100 / 5}%`,
        flexBasis: `${100 / 5}%`,
      },
    }),

  '@media print': {
    flexBasis: '100%',
    maxWidth: '100%',
    padding: theme.spacing(0.25, 1.5),
    textAlign: 'left',
  },
}));

type ItemCodeProps = {
    isAuthFlowStep?: boolean;
};

export const ItemCode = styled(Text, {
  shouldForwardProp: prop => prop !== 'isAuthFlowStep',
})<ItemCodeProps>(({theme, isAuthFlowStep}) => ({
  color: alpha(theme.palette.black, 0.6),
  fontWeight: 600,
  fontSize: 18,
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  [theme.breakpoints.up('md')]: isAuthFlowStep ? {
    fontSize: 16,
    overflowWrap: 'normal',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
  } : undefined,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
  },

  '@media print': {
    fontFamily: 'monospace',
    fontSize: 13,
    overflowWrap: 'normal',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
  },
}));

export const NoBackupCodesText = styled(Text)(({theme}) => ({
  padding: `${theme.spacing(3)} 0`,
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

export const PrintLogo = styled('div')(({theme}) => ({
  paddingBottom: theme.spacing(4),
  width: '100%',
}));

export const Logo = styled('img')({
  display: 'block',
  height: 'auto',
  maxWidth: '100%',
  width: 180,
});

export const BackupCodesTitle = styled(Text)(({theme}) => ({
  paddingBottom: theme.spacing(1.5),
}));

export const BackupCodesSubtitle = styled(Text)(({theme}) => ({
  margin: theme.spacing(2, 0),
}));

export const BackupCodesSupportingText = styled(Text)(({theme}) => ({
  color: alpha(theme.palette.common.black, 0.7),
}));

// PrintableBackupCodes styles
export const PrintableContainer = styled(Grid)({
  display: 'flex',
});

export const StyledPrintGrid = styled(Grid)(({theme}) => ({
  backgroundColor: theme.palette.background.paper,
  boxSizing: 'border-box',
  padding: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

export const HiddenGrid = styled(Grid)({
  display: 'none',
});

export const CenterContainer = styled(Grid)({
  alignItems: 'center',
});

export const GridItem = styled(Grid)(({theme}) => ({
  alignItems: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '&.compact': {
    paddingRight: 0,
  },
  '&.regular': {
    paddingRight: theme.spacing(2),
  },
}));
